import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { AIChatMessage, AIChatMode } from '../ai-chat/types';
import { Tooltip } from '../tooltip/Tooltip';
import { AIChatPillAccordion } from './AIChatPillAccordion';
import type { AIChatClearHistoryLabels } from './AIChatPillAccordion';
import {
	DropdownRoot,
	DropdownTrigger,
	TriggerSparklesIcon,
	DropdownOverlay,
	BadgeWrapper,
	ThinkingBadge,
	ThinkingDot,
	UnreadBadge,
} from './AIChatDropdown.Styled';

export type AIChatDropdownProps = {
    /** Trigger button label */
    label?: string;
    /** Controlled open state */
    isOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    // AIChatPillAccordion props
    title: string;
    subtitle?: string;
    messages: AIChatMessage[];
    isSending?: boolean;
    onSend: (value: string) => void;
    placeholder?: string;
    modes?: AIChatMode[];
    activeModeId?: string;
    onModeChange?: (modeId: string) => void;
    assistantAvatarSrc?: string;
    assistantAvatarAlt?: string;
    autoScroll?: boolean;
    emptyState?: ReactNode;
    /** Width of the overlay panel */
    chatWidth?: string | number;
    /** Height of the message list inside the accordion */
    chatHeight?: string | number;
    /** Shows red unread dot when chat is closed and a new response arrived */
    hasUnread?: boolean;
    /** Tooltip shown on thinking badge */
    thinkingTooltip?: string;
    /** Tooltip shown on unread badge */
    unreadTooltip?: string;
    /** Called after the user confirms deletion. The header button is hidden while the chat is empty. */
    onClearHistory: () => void;
    clearHistoryLabels?: AIChatClearHistoryLabels;
};

export function AIChatDropdown({
	label = 'Build with AI',
	isOpen: isOpenProp,
	onOpenChange,
	title,
	subtitle,
	messages,
	isSending,
	onSend,
	placeholder,
	modes,
	activeModeId,
	onModeChange,
	assistantAvatarSrc,
	assistantAvatarAlt,
	autoScroll,
	emptyState,
	chatWidth = 400,
	chatHeight,
	hasUnread = false,
	thinkingTooltip = 'AI is thinking…',
	unreadTooltip = 'AI has responded',
	onClearHistory,
	clearHistoryLabels,
}: AIChatDropdownProps): React.ReactElement {
	const [localOpen, setLocalOpen] = useState(false);
	const isControlled = isOpenProp !== undefined;
	const isOpen = isControlled ? isOpenProp : localOpen;

	const rootRef = useRef<HTMLDivElement>(null);

	const toggle = useCallback(() => {
		const next = !isOpen;
		if (!isControlled) setLocalOpen(next);
		onOpenChange?.(next);
	}, [isOpen, isControlled, onOpenChange]);

	const close = useCallback(() => {
		if (!isControlled) setLocalOpen(false);
		onOpenChange?.(false);
	}, [isControlled, onOpenChange]);

	useEffect(() => {
		if (!isOpen) return;
		const handlePointerDown = (e: MouseEvent) => {
			const target = e.target as Element;
			// The clear-history dialog portals to <body>, so it sits outside rootRef —
			// don't treat clicks inside it as outside clicks.
			if (target.closest?.('.ReactModalPortal')) return;
			if (rootRef.current && !rootRef.current.contains(target)) {
				close();
			}
		};
		document.addEventListener('mousedown', handlePointerDown);
		return () => document.removeEventListener('mousedown', handlePointerDown);
	}, [isOpen, close]);

	return (
		<DropdownRoot ref={rootRef}>
			<DropdownTrigger
				type="button"
				$isOpen={isOpen}
				onClick={toggle}
				aria-expanded={isOpen}
				aria-haspopup="dialog"
			>
				<TriggerSparklesIcon icon="sparkles-regular" aria-hidden="true" />
				{label}
			</DropdownTrigger>
			{!isOpen && isSending && (
				<Tooltip message={thinkingTooltip} placement="top">
					<BadgeWrapper aria-label={thinkingTooltip}>
						<ThinkingBadge>
							<ThinkingDot $delay={0} />
							<ThinkingDot $delay={200} />
							<ThinkingDot $delay={400} />
						</ThinkingBadge>
					</BadgeWrapper>
				</Tooltip>
			)}
			{!isOpen && !isSending && hasUnread && (
				<Tooltip message={unreadTooltip} placement="top">
					<BadgeWrapper aria-label={unreadTooltip}>
						<UnreadBadge />
					</BadgeWrapper>
				</Tooltip>
			)}
			<DropdownOverlay $width={chatWidth} $isOpen={isOpen} aria-hidden={!isOpen}>
				<AIChatPillAccordion
					title={title}
					subtitle={subtitle}
					messages={messages}
					isSending={isSending}
					onSend={onSend}
					placeholder={placeholder}
					modes={modes}
					activeModeId={activeModeId}
					onModeChange={onModeChange}
					assistantAvatarSrc={assistantAvatarSrc}
					assistantAvatarAlt={assistantAvatarAlt}
					autoScroll={autoScroll}
					emptyState={emptyState}
					chatHeight={chatHeight}
					onClearHistory={onClearHistory}
					clearHistoryLabels={clearHistoryLabels}
				/>
			</DropdownOverlay>
		</DropdownRoot>
	);
}
